/**
 * Form Validation Utilities
 * Client-side validation matching backend requirements
 */

export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Validate email format
 */
export const validateEmail = (email: string): ValidationResult => {
  if (!email) {
    return { isValid: false, error: 'Email is required' };
  }

  // RFC 5322 compliant email regex (simplified version)
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!emailRegex.test(email)) {
    return { isValid: false, error: 'Please enter a valid email address' };
  }

  return { isValid: true };
};

/**
 * Validate password strength
 * Requirements:
 * - Minimum 12 characters
 * - At least 2 uppercase letters
 * - At least 2 lowercase letters
 * - At least 2 numbers
 * - At least 2 special characters
 */
export const validatePassword = (password: string): ValidationResult => {
  if (!password) {
    return { isValid: false, error: 'Password is required' };
  }

  if (password.length < 12) {
    return {
      isValid: false,
      error: 'Password must be at least 12 characters long'
    };
  }

  const uppercase = password.match(/[A-Z]/g) || [];
  if (uppercase.length < 2) {
    return {
      isValid: false,
      error: 'Password must contain at least 2 uppercase letters'
    };
  }

  const lowercase = password.match(/[a-z]/g) || [];
  if (lowercase.length < 2) {
    return {
      isValid: false,
      error: 'Password must contain at least 2 lowercase letters'
    };
  }

  const numbers = password.match(/[0-9]/g) || [];
  if (numbers.length < 2) {
    return {
      isValid: false,
      error: 'Password must contain at least 2 numbers'
    };
  }

  // eslint-disable-next-line no-useless-escape
  const special = password.match(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/g) || [];
  if (special.length < 2) {
    return {
      isValid: false,
      error: 'Password must contain at least 2 special characters'
    };
  }

  return { isValid: true };
};

/**
 * Validate password confirmation
 */
export const validatePasswordConfirmation = (
  password: string,
  confirmPassword: string
): ValidationResult => {
  if (!confirmPassword) {
    return { isValid: false, error: 'Please confirm your password' };
  }

  if (password !== confirmPassword) {
    return { isValid: false, error: 'Passwords do not match' };
  }

  return { isValid: true };
};

/**
 * Validate name (first name or last name)
 */
export const validateName = (name: string, fieldName: string = 'Name'): ValidationResult => {
  if (!name || !name.trim()) {
    return { isValid: false, error: `${fieldName} is required` };
  }

  if (name.trim().length < 2) {
    return {
      isValid: false,
      error: `${fieldName} must be at least 2 characters long`
    };
  }

  if (name.trim().length > 50) {
    return {
      isValid: false,
      error: `${fieldName} must not exceed 50 characters`
    };
  }

  // Only allow letters, spaces, hyphens, and apostrophes
  if (!/^[a-zA-Z\s'-]+$/.test(name)) {
    return {
      isValid: false,
      error: `${fieldName} can only contain letters, spaces, hyphens, and apostrophes`
    };
  }

  return { isValid: true };
};

/**
 * Get detailed password validation errors (for real-time feedback)
 */
export const getPasswordValidationErrors = (password: string): string[] => {
  const errors: string[] = [];

  if (password.length < 12) {
    errors.push('Must be at least 12 characters');
  }

  const uppercase = password.match(/[A-Z]/g) || [];
  if (uppercase.length < 2) {
    errors.push('Must contain at least 2 uppercase letters');
  }

  const lowercase = password.match(/[a-z]/g) || [];
  if (lowercase.length < 2) {
    errors.push('Must contain at least 2 lowercase letters');
  }

  const numbers = password.match(/[0-9]/g) || [];
  if (numbers.length < 2) {
    errors.push('Must contain at least 2 numbers');
  }

  // eslint-disable-next-line no-useless-escape
  const special = password.match(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/g) || [];
  if (special.length < 2) {
    errors.push('Must contain at least 2 special characters');
  }

  return errors;
};

/**
 * Get password strength indicator
 * Returns: weak, medium, strong
 */
export const getPasswordStrength = (password: string): 'weak' | 'medium' | 'strong' => {
  if (!password) return 'weak';

  let strength = 0;

  // Length check
  if (password.length >= 8) strength++;
  if (password.length >= 12) strength++;
  if (password.length >= 16) strength++;

  // Character variety checks (check for 2+ of each)
  const uppercase = password.match(/[A-Z]/g) || [];
  if (uppercase.length >= 2) strength++;

  const lowercase = password.match(/[a-z]/g) || [];
  if (lowercase.length >= 2) strength++;

  const numbers = password.match(/[0-9]/g) || [];
  if (numbers.length >= 2) strength++;

  // eslint-disable-next-line no-useless-escape
  const special = password.match(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/g) || [];
  if (special.length >= 2) strength++;

  if (strength <= 3) return 'weak';
  if (strength <= 5) return 'medium';
  return 'strong';
};

/**
 * Validate required text field
 */
export const validateRequired = (value: string, fieldName: string): ValidationResult => {
  if (!value || !value.trim()) {
    return { isValid: false, error: `${fieldName} is required` };
  }
  return { isValid: true };
};

/**
 * Format a phone string for display as (XXX) XXX-XXXX.
 * Strips non-digits from any stored format before reformatting.
 */
export const formatPhone = (value?: string | null): string => {
  if (!value) return '';
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

/**
 * Apply live phone masking to an onChange value so the input shows (XXX) XXX-XXXX as the user types.
 */
export const maskPhoneInput = (value: string): string => formatPhone(value);

/**
 * Validate phone number
 */
export const validatePhone = (phone: string): ValidationResult => {
  if (!phone || !phone.trim()) {
    return { isValid: true };
  }

  // Remove common formatting characters
  const cleaned = phone.replace(/[\s\-().]/g, '');

  // Check if it contains only digits and optional + prefix
  if (!/^\+?\d{10,15}$/.test(cleaned)) {
    return {
      isValid: false,
      error: 'Please enter a valid phone number (10-15 digits)'
    };
  }

  return { isValid: true };
};

/**
 * Validate ZIP/postal code
 */
export const validateZipCode = (zipCode: string): ValidationResult => {
  if (!zipCode || !zipCode.trim()) {
    return { isValid: false, error: 'ZIP code is required' };
  }

  // Support US ZIP codes (5 digits or 5+4 format) and international postal codes
  if (!/^[\d\w\s-]{3,20}$/.test(zipCode.trim())) {
    return {
      isValid: false,
      error: 'Please enter a valid ZIP/postal code'
    };
  }

  return { isValid: true };
};

/**
 * Validate company name
 */
export const validateCompanyName = (name: string): ValidationResult => {
  if (!name || !name.trim()) {
    return { isValid: false, error: 'Company name is required' };
  }

  if (name.trim().length < 2) {
    return {
      isValid: false,
      error: 'Company name must be at least 2 characters long'
    };
  }

  if (name.trim().length > 200) {
    return {
      isValid: false,
      error: 'Company name must not exceed 200 characters'
    };
  }

  return { isValid: true };
};
